import { useCallback, useEffect, useState } from 'react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { saveTemplate, listTemplates, updateTemplate, type Template } from './api'
import { Parts } from './components'

/**
 * The messages campaigns send.
 *
 * ## The part counter is on this screen and not in a report
 *
 * An SMS is 160 GSM-7 characters. One emoji, or one curly apostrophe pasted out of a word
 * processor, makes the whole message UCS-2 at 70 — so a 150-character message that "looks
 * fine" can cost three times what the campaign budgeted, per recipient. The counter updates as
 * the operator types, which is the only moment the information is worth anything.
 *
 * ## The opt-out footer is part of the template, not the campaign
 *
 * If it lived on the campaign it would be forgotten by whoever is composing at 5am, and a
 * message with no way out of it is the one that generates the complaint.
 */
export function TemplatesPage() {
  const [rows, setRows] = useState<Template[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<Template | null>(null)
  const [adding, setAdding] = useState(false)

  const [code, setCode] = useState('')
  const [channelCode, setChannelCode] = useState('SMS')
  const [displayName, setDisplayName] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [footer, setFooter] = useState('Reply STOP to opt out.')

  const load = useCallback(async () => {
    try {
      setError(null)
      setRows(await listTemplates())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (error && !rows) return <ErrorStrip message={error} />
  if (!rows) return <LoadingBlock label="Loading templates" />

  function startNew() {
    setEditing(null); setAdding(true)
    setCode(''); setChannelCode('SMS'); setDisplayName(''); setSubject(''); setBody('')
    setFooter('Reply STOP to opt out.')
  }

  function startEdit(t: Template) {
    setEditing(t); setAdding(true)
    setCode(t.code); setChannelCode(t.channelCode); setDisplayName(t.displayName)
    setSubject(t.subject ?? ''); setBody(t.body); setFooter(t.footer ?? '')
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true); setError(null)
    const payload = {
      code: code.trim(), channelCode, displayName: displayName.trim(),
      subject: channelCode === 'EMAIL' ? subject.trim() : null,
      body: body.trim(), footer: footer.trim() || null, languageCode: 'en', active: true,
    }
    try {
      if (editing) {
        await updateTemplate(editing.id, payload)
      } else {
        await saveTemplate(payload)
      }
      setAdding(false); setEditing(null)
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  // Mirrors TextSmsChannel.segmentsFor closely enough for a live hint. The BILLED number is
  // the server's — this one exists so the operator sees the cliff while typing, not after.
  const gsm7 = /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0-9:;<=>?¡A-ZÄÖÑÜ§¿a-zäöñüà^{}\\[~\]|€]*$/
  const isGsm = gsm7.test(body)
  const liveParts = body.length === 0 ? 1
    : isGsm
      ? (body.length <= 160 ? 1 : Math.ceil(body.length / 153))
      : (body.length <= 70 ? 1 : Math.ceil(body.length / 67))

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Templates"
        description="What campaigns say. Written once, rendered per person, and frozen onto
                     every recipient row at send time."
      />

      {error && <ErrorStrip message={error} />}

      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-2 p-2">
          <p className="text-[11px] text-muted-foreground">
            {rows.length} template{rows.length === 1 ? '' : 's'}.
          </p>
          <button type="button" onClick={() => (adding ? setAdding(false) : startNew())}
                  className="rounded border border-border px-2 py-1 text-[11px]">
            {adding ? 'Close' : 'Write a template'}
          </button>
        </div>

        {adding && (
          <form onSubmit={submit} className="space-y-2 border-t border-border p-3">
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Code
                </span>
                <input value={code} onChange={(e) => setCode(e.target.value)} required
                       placeholder="gotv.reminder"
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Channel
                </span>
                <select value={channelCode} onChange={(e) => setChannelCode(e.target.value)}
                        className="h-7 rounded border border-input bg-background px-2
                                   text-[12px]">
                  <option value="SMS">SMS</option>
                  <option value="EMAIL">Email</option>
                </select>
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Call it
                </span>
                <input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                       required
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
            </div>
            {channelCode === 'EMAIL' && (
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Subject
                </span>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} required
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
                <span className="text-[10px] text-muted-foreground">
                  An email with no subject arrives as “(no subject)” and is deleted unread.
                </span>
              </label>
            )}
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                The message
              </span>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} required
                        placeholder="Hi {{firstName}}, polling opens at 6am."
                        className="rounded border border-input bg-background px-2 py-1
                                   text-[12px]" />
              {channelCode === 'SMS' && (
                <>
                  <Parts parts={liveParts} characters={body.length} />
                  {!isGsm && body.length > 0 && (
                    <span className="text-[10px] text-amber-700 dark:text-amber-400">
                      This message contains a character outside the basic SMS alphabet — an
                      emoji, or an apostrophe pasted from a word processor. That drops the whole
                      message to 70 characters a part, for every recipient.
                    </span>
                  )}
                </>
              )}
              <span className="text-[10px] text-muted-foreground">
                Use {'{{firstName}}'} or {'{{name}}'}. A recipient with no value for a
                placeholder is counted as unreachable rather than sent the message with the
                placeholder still in it.
              </span>
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Opt-out footer
              </span>
              <input value={footer} onChange={(e) => setFooter(e.target.value)}
                     className="h-7 rounded border border-input bg-background px-2
                                text-[12px]" />
              <span className="text-[10px] text-muted-foreground">
                Appended to every message. Leaving it empty falls back to the platform default —
                there is no path that sends a campaign message with no way out of it.
              </span>
            </label>
            <button type="submit" disabled={busy || !code.trim() || !body.trim()}
                    className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                               text-primary-foreground disabled:opacity-50">
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Save the template'}
            </button>
          </form>
        )}
      </Panel>

      <ul className="grid gap-2 lg:grid-cols-2">
        {rows.map((t) => (
          <li key={t.id}>
            <Panel>
              <div className="space-y-1 p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <span className="text-[12px] font-medium">{t.displayName}</span>
                    <span className="block text-[10px] text-muted-foreground">
                      {t.code} · {t.channelCode}
                    </span>
                  </div>
                  <button type="button" onClick={() => startEdit(t)}
                          className="rounded border border-border px-2 py-0.5 text-[10px]">
                    Edit
                  </button>
                </div>
                {t.subject && <p className="text-[12px] font-medium">{t.subject}</p>}
                <p className="whitespace-pre-wrap rounded border border-border bg-muted/20 p-2
                              text-[12px]">{t.body}</p>
                {t.footer && (
                  <p className="text-[10px] text-muted-foreground">Ends with: “{t.footer}”</p>
                )}
                <Parts parts={t.estimatedParts} characters={t.body.length} />
                {t.placeholders.length > 0 && (
                  <p className="text-[10px] text-muted-foreground">
                    Needs {t.placeholders.join(', ')} per person.
                  </p>
                )}
              </div>
            </Panel>
          </li>
        ))}
      </ul>
    </div>
  )
}
